import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Zap, BatteryCharging, Sun, Activity, ArrowRight, X } from 'lucide-react';

import minigridAPI from '../../../service/minigridAPI';
import momiAPI from '../../../../momi/service/momiAPI';
import { getMomiTelemetryLastMeasurement } from '../../../service/momiTelemetryAPI';
import { lastPoint } from '../../../../../common/utils/metricFormat';
import { Minigrid, MinigridLiveSnapshot } from '../../../types/minigridtypes';
import { minigridMapState } from '../../../redux/minigrid.mapstate';
import { useAuth } from '../../../../auth/hooks';
import styles from './MinigridListMap.module.scss';

const MAP_PAGE_SIZE = 1000;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LIVE_POLL_INTERVAL_MS = 60000;

const hasValidCoords = (m: Minigrid): boolean => {
  const lat = m.minigrid_location_lat;
  const lng = m.minigrid_location_lng;
  if (lat === null || lat === undefined || lng === null || lng === undefined) return false;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  return true;
};

function RecenterOnChange({ center, zoom }: { center?: [number, number]; zoom?: number }) {
  const map = useMap();
  const lat = center?.[0];
  const lng = center?.[1];
  useEffect(() => {
    if (center && zoom !== undefined) {
      map.setView(center, zoom);
    }
  }, [lat, lng, zoom, map, center]);
  return null;
}

const ICON_SIZE = 14;

function wattsToKw(w: number | null | undefined): string | null {
  if (w === null || w === undefined || Number.isNaN(w)) return null;
  return (w / 1000).toFixed(2);
}

function numberOrNull(v: number | string | undefined): number | null {
  if (v === undefined || v === null) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
}

function socTier(soc: number | null): 'high' | 'mid' | 'low' | 'unknown' {
  if (soc === null) return 'unknown';
  if (soc >= 50) return 'high';
  if (soc >= 20) return 'mid';
  return 'low';
}

// Marker badge: circular card with "SoC batterie", big percent, and a status
// pill. Built as raw HTML so Leaflet's divIcon can inject it without a React
// renderer inside the Leaflet overlay pane.
function buildMarkerHtml(soc: number | null, isOnline: boolean, loading: boolean): string {
  const tier = socTier(soc);
  const ringColor =
    tier === 'high' ? '#16a34a'
      : tier === 'mid' ? '#d97706'
      : tier === 'low' ? '#dc2626'
      : '#94a3b8';
  const socText = soc === null ? '—' : `${soc.toFixed(0)}%`;
  const pillBg = loading ? '#e2e8f0' : isOnline ? '#16a34a' : '#dc2626';
  const pillLabel = loading ? 'Synchro' : isOnline ? 'En ligne' : 'Hors ligne';
  const pillDot = loading ? '#94a3b8' : '#ffffff';
  // `color` on the outer badge drives `currentColor` for the downward arrow
  // (drawn in CSS via ::after). `border-color` is set alongside so the ring
  // also follows the SoC tier hue.
  return `
    <div class="${styles['pinBadge']}" style="border-color:${ringColor};color:${ringColor}">
      <div class="${styles['pinBadge__label']}">SoC batterie</div>
      <div class="${styles['pinBadge__value']}" style="color:${ringColor}">${socText}</div>
      <div class="${styles['pinBadge__pill']}" style="background:${pillBg};color:#ffffff">
        <span class="${styles['pinBadge__pillDot']}" style="background:${pillDot}"></span>
        ${pillLabel}
      </div>
    </div>
  `;
}

function MinigridTooltipContent({
  minigrid,
  snap,
  voltage,
  loading,
  pinned,
  onClose,
  onNavigate,
}: {
  minigrid: Minigrid;
  snap: MinigridLiveSnapshot | undefined;
  voltage: number | null;
  loading: boolean;
  pinned: boolean;
  onClose: () => void;
  onNavigate: () => void;
}) {
  const soc = snap ? snap.battery_soc_percent : null;
  const solarKw = wattsToKw(snap?.solar_power_w);
  const consumptionKw = wattsToKw(snap?.consumption_w);
  const isOnline = snap?.connection_status === 'online';
  const tier = socTier(soc);

  const socBarClass =
    tier === 'high' ? styles['tooltip__bar--high']
      : tier === 'mid' ? styles['tooltip__bar--mid']
      : tier === 'low' ? styles['tooltip__bar--low']
      : styles['tooltip__bar--unknown'];

  const renderValue = (v: string | null, unit: string) =>
    v === null ? (
      <span className={styles['tooltip__placeholder']}>—</span>
    ) : (
      <>
        <span>{v}</span>
        <span className={styles['tooltip__unit']}>{unit}</span>
      </>
    );

  const statusLabel = loading ? 'Synchro' : isOnline ? 'EN LIGNE' : 'HORS LIGNE';
  const statusClass = loading
    ? styles['tooltip__badge--loading']
    : isOnline
      ? styles['tooltip__badge--online']
      : styles['tooltip__badge--offline'];

  // When pinned, prevent clicks inside the card from reaching the map —
  // otherwise the map's click handler unpins the tooltip before React's
  // onClick can process the navigation/close action. L.DomEvent hooks the
  // native capture phase, which stopPropagation on React synthetic events
  // cannot reach.
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !pinned) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, [pinned]);

  return (
    <div ref={cardRef} className={styles['tooltip']}>
      <div className={styles['tooltip__head']}>
        <div className={styles['tooltip__name']}>{minigrid.minigrid_name}</div>
        <div className={`${styles['tooltip__badge']} ${statusClass}`}>
          <span className={styles['tooltip__badgeDot']} />
          {statusLabel}
        </div>
        {pinned && (
          <button
            type="button"
            className={styles['tooltip__close']}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Fermer"
          >
            <X size={13} strokeWidth={2.4} />
          </button>
        )}
      </div>

      <div className={styles['tooltip__rows']}>
        <div className={styles['tooltip__row']}>
          <span className={`${styles['tooltip__icon']} ${styles['tooltip__icon--voltage']}`}>
            <Zap size={ICON_SIZE} strokeWidth={2.2} />
          </span>
          <span className={styles['tooltip__label']}>Tension moy.</span>
          <span className={styles['tooltip__value']}>
            {renderValue(voltage !== null ? voltage.toFixed(1) : null, 'V')}
          </span>
        </div>

        <div className={styles['tooltip__rowSoc']}>
          <div className={styles['tooltip__row']}>
            <span className={`${styles['tooltip__icon']} ${styles['tooltip__icon--battery']}`}>
              <BatteryCharging size={ICON_SIZE} strokeWidth={2.2} />
            </span>
            <span className={styles['tooltip__label']}>SoC batterie</span>
            <span className={styles['tooltip__value']}>
              {renderValue(soc !== null ? soc.toFixed(0) : null, '%')}
            </span>
          </div>
          <div className={styles['tooltip__barTrack']}>
            <div
              className={`${styles['tooltip__bar']} ${socBarClass}`}
              style={{ width: soc === null ? 0 : `${Math.max(0, Math.min(100, soc))}%` }}
            />
            {soc !== null && (
              <span className={styles['tooltip__barLabel']}>{soc.toFixed(0)}%</span>
            )}
          </div>
        </div>

        <div className={styles['tooltip__row']}>
          <span className={`${styles['tooltip__icon']} ${styles['tooltip__icon--solar']}`}>
            <Sun size={ICON_SIZE} strokeWidth={2.2} />
          </span>
          <span className={styles['tooltip__label']}>Production solaire</span>
          <span className={styles['tooltip__value']}>
            {renderValue(solarKw, 'kW')}
          </span>
        </div>

        <div className={styles['tooltip__row']}>
          <span className={`${styles['tooltip__icon']} ${styles['tooltip__icon--consumption']}`}>
            <Activity size={ICON_SIZE} strokeWidth={2.2} />
          </span>
          <span className={styles['tooltip__label']}>Consommation</span>
          <span className={styles['tooltip__value']}>
            {renderValue(consumptionKw, 'kW')}
          </span>
        </div>
      </div>

      {pinned && (
        <button
          type="button"
          className={styles['tooltip__cta']}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate();
          }}
        >
          <span>Voir la minigrid</span>
          <ArrowRight size={14} strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}

function MinigridPin({
  minigrid,
  pinned,
  onTogglePin,
  onUnpin,
}: {
  minigrid: Minigrid;
  pinned: boolean;
  onTogglePin: () => void;
  onUnpin: () => void;
}) {
  const navigate = useNavigate();

  const liveQuery = useQuery({
    queryKey: ['minigrid', minigrid.minigrid_id, 'live', 'map'],
    queryFn: () => minigridAPI.getMinigridLive(minigrid.minigrid_id),
    refetchInterval: LIVE_POLL_INTERVAL_MS,
    staleTime: LIVE_POLL_INTERVAL_MS / 2,
  });

  const momiId = minigrid.minigrid_momi_id?.trim() || '';
  const momiQuery = useQuery({
    queryKey: ['momi-for-map', momiId],
    queryFn: () => momiAPI.getSingleMomi(momiId),
    enabled: Boolean(momiId),
    staleTime: 5 * 60 * 1000,
  });
  const momiUuid = momiQuery.data?.momi_uuid?.trim() || '';

  const voltageQuery = useQuery({
    queryKey: ['momi-telemetry-last', momiUuid, 'voltage_l_n_avg', 'map'],
    queryFn: () => getMomiTelemetryLastMeasurement(momiUuid, 'voltage_l_n_avg'),
    enabled: Boolean(momiUuid),
    refetchInterval: LIVE_POLL_INTERVAL_MS,
    staleTime: LIVE_POLL_INTERVAL_MS / 2,
  });

  const snap = liveQuery.data;
  const voltage = numberOrNull(lastPoint(voltageQuery.data)?.Value);
  const soc = snap ? snap.battery_soc_percent : null;
  const isOnline = snap?.connection_status === 'online';
  const loading = liveQuery.isLoading && !snap;

  const icon = useMemo(
    () =>
      L.divIcon({
        className: styles['pinBadgeWrapper'],
        html: buildMarkerHtml(soc, isOnline, loading),
        // Height accounts for the downward arrow (~9px). Anchor is at the
        // bottom-center so the arrow tip sits exactly on the coordinate.
        iconSize: [56, 65],
        iconAnchor: [28, 65],
      }),
    [soc, isOnline, loading],
  );

  return (
    <Marker
      position={[minigrid.minigrid_location_lat, minigrid.minigrid_location_lng]}
      icon={icon}
      eventHandlers={{
        click: () => onTogglePin(),
      }}
    >
      <Tooltip
        // Re-key on pinned transition so Leaflet rebuilds the overlay with
        // the new permanent/interactive flags (these are not reactive).
        key={pinned ? 'pinned' : 'hover'}
        direction="right"
        // Anchor is at the arrow tip (bottom). Shift the tooltip right and
        // up so it lines up with the circle center instead of the tip.
        offset={[32, -32]}
        opacity={1}
        permanent={pinned}
        interactive={pinned}
        sticky={!pinned}
        className={
          pinned
            ? 'minigrid-live-tooltip minigrid-live-tooltip--pinned'
            : 'minigrid-live-tooltip'
        }
      >
        <MinigridTooltipContent
          minigrid={minigrid}
          snap={snap}
          voltage={voltage}
          loading={loading}
          pinned={pinned}
          onClose={onUnpin}
          onNavigate={() => navigate(`/main/minigrid/${minigrid.minigrid_id}`)}
        />
      </Tooltip>
    </Marker>
  );
}

function MapClickUnpin({ onUnpin }: { onUnpin: () => void }) {
  useMapEvents({
    click: () => onUnpin(),
  });
  return null;
}

export default function MinigridListMap() {
  const { minigrid: minigridState } = useSelector(minigridMapState);
  const { authState } = useAuth();
  const accountId = authState.authData?.accountId || null;
  const querySearch = minigridState.querySearch || null;
  const [pinnedId, setPinnedId] = useState<string | null>(null);

  const mapQuery = useQuery({
    queryKey: ['minigrid-map-all', querySearch, accountId],
    queryFn: () =>
      minigridAPI.getAllMinigrids(1, MAP_PAGE_SIZE, {
        querySearch,
        accountId,
      }),
  });

  const data = mapQuery.data;
  const isLoading = mapQuery.isLoading;

  const pins = useMemo(() => {
    const list = data?.data || [];
    return list.filter(hasValidCoords);
  }, [data]);

  const center = useMemo<[number, number] | undefined>(() => {
    if (pins.length === 0) return undefined;
    const sumLat = pins.reduce((acc, m) => acc + m.minigrid_location_lat, 0);
    const sumLng = pins.reduce((acc, m) => acc + m.minigrid_location_lng, 0);
    return [sumLat / pins.length, sumLng / pins.length];
  }, [pins]);

  return (
    <div className={styles['card']}>
      <div className={styles['card__header']}>
        <span className={styles['card__title']}>Localisation des minigrids</span>
        <span className={styles['card__count']}>
          {pins.length} minigrid{pins.length > 1 ? 's' : ''} localisé
          {pins.length > 1 ? 's' : ''}
        </span>
      </div>

      {isLoading ? (
        <div className={styles['card__empty']}>Chargement…</div>
      ) : pins.length === 0 || !center ? (
        <div className={styles['card__empty']}>
          Aucun minigrid géolocalisé sur cette page
        </div>
      ) : (
        <div className={styles['card__map']}>
          <MapContainer
            center={center}
            zoom={6}
            style={{
              height: '100%',
              width: '100%',
              borderRadius: '12px',
              position: 'relative',
            }}
          >
            <RecenterOnChange center={center} zoom={6} />
            <MapClickUnpin onUnpin={() => setPinnedId(null)} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {pins.map((m) => (
              <MinigridPin
                key={m.minigrid_id}
                minigrid={m}
                pinned={pinnedId === m.minigrid_id}
                onTogglePin={() =>
                  setPinnedId((prev) => (prev === m.minigrid_id ? null : m.minigrid_id))
                }
                onUnpin={() => setPinnedId(null)}
              />
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
}
